import React, { useMemo } from "react";

import type { ProcessedSelection } from "@/hooks/firestore/betting/useEntries";

import cx from "classnames";
import { getSelectionStatus } from "../../util/selection-helpers";
import { Clamp } from "@picklebet/ui";
import { LiveClampWithStream } from "../common/LiveClampWithStream";
import { WinCelebration } from "../common/WinCelebration";
import { Countdown } from "@/features/shell/components/shared/Countdown";
import { parseISO } from "date-fns/parseISO";
import { isOutright } from "utilities/sharedBettingUtilities";
import {
  isExoticSelection,
  isQuaddieSelection,
  isSameEventMultiSelection,
  isSingleSelection,
} from "features/entries/util/type-guards";

import * as styles from "./SelectionClamp.module.scss";

interface SelectionClampProps {
  selection: ProcessedSelection;
  isPreview?: boolean;
  isEntryWon?: boolean;
}

const SelectionClamp = ({
  selection,
  isPreview = false,
  isEntryWon = false,
}: SelectionClampProps) => {
  const status = getSelectionStatus(selection);
  const isVoid = selection.void || selection.isVoid;
  const hasSettled = selection.result !== "UNDECIDED";
  const isLive = status?.message === "LIVE";
  const shouldShowCountdown = !status;
  const isSEM = isSameEventMultiSelection(selection);
  const isRace = selection.event.eventType === "RACE";

  const isSelectionOutright = isOutright(selection.event.eventType);

  const startTime =
    isSelectionOutright && selection.marketNextBetStop
      ? selection.marketNextBetStop
      : selection.event.scheduledStartTime;

  const showStatus = useMemo(() => {
    if (!status || !(isVoid || hasSettled)) return false;
    if (isSEM) return status.variant === "success" || isVoid;
    return true;
  }, [isVoid, hasSettled, status, isSEM]);

  const showScratched = useMemo(() => {
    if (!isRace || !isVoid || isSEM) return false;

    if (isExoticSelection(selection)) {
      return false;
    }

    if (isQuaddieSelection(selection)) {
      return true;
    }

    if (isSingleSelection(selection)) {
      return true;
    }

    return false;
  }, [isRace, isVoid, isSEM, selection]);

  return (
    <>
      {shouldShowCountdown && (
        <Countdown
          className={cx({
            [styles.noMouseEvents]: isPreview,
          })}
          scheduledStartTime={parseISO(startTime ?? "")}
          selection={selection}
        />
      )}
      {!hasSettled && (
        <LiveClampWithStream
          className={cx({
            [styles.noMouseEvents]: isPreview,
          })}
          isLive={isLive}
          selection={selection}
        />
      )}
      {showStatus && status && (
        <Clamp variant={status.variant}>
          <WinCelebration
            isEnabled={status.variant === "success" && isEntryWon}
          >
            {showScratched ? "SCRATCHED" : status.message}
          </WinCelebration>
        </Clamp>
      )}
    </>
  );
};

export { SelectionClamp };
